import {
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonMenuToggle,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { captureEvent } from '@sentry/react';
import { warningOutline } from 'ionicons/icons';
import {
  ReactElement,
  ReactNode,
  forwardRef,
  useCallback,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Attachment } from '../../api/graphql/generated';
import { BookmarkType } from '../../api/hooks/bookmark.hooks';
import { useHasFeature } from '../../api/hooks/feature.hooks';
import { useCurrentUserProp } from '../../api/user.hooks';
import { Avatar } from '../avatar/avatar';
import { BookmarkToggle } from '../bookmarks/bookmarks-toggle';
import { CustomBackButton } from './back-button';
import './header.css';
import { UnreadBadge } from './unread-badge';

export interface HeaderBookmarkProps {
  enable: boolean;
  type: BookmarkType;
}

export interface HeaderProps {
  title?: string | null;
  subtitle?: string | null;
  children?: ReactNode;
  secondaryToolbar?: ReactNode;
  rightButtons?: ReactElement;
  backgroundColor?: string;
  bookmarkProps?: HeaderBookmarkProps;
}

export interface NestedHeaderProps extends HeaderProps {
  defaultBackRoute: string;
  bookmarkProps?: HeaderBookmarkProps;
}

const pageTitleBuilder = (
  title?: string | null,
  subtitle?: string | null
): ReactElement => {
  if (subtitle) {
    return (
      <IonTitle>
        {title}
        <div className="subtitle">{subtitle}</div>
      </IonTitle>
    );
  } else {
    return <IonTitle>{title}</IonTitle>;
  }
};

const PageHeader = forwardRef<HTMLIonHeaderElement, HeaderProps>(
  (props: HeaderProps, ref): ReactElement => {
    const {
      children,
      title,
      subtitle,
      secondaryToolbar,
      rightButtons,
      bookmarkProps,
    } = props;

    const avatar = useCurrentUserProp<Attachment>('avatarAttachment');
    const displayBookmarkToggle = bookmarkProps?.enable && title !== undefined;

    return (
      <IonHeader ref={ref} className="primary-header">
        <IonToolbar className="primary-toolbar">
          <IonButtons slot="start">
            <IonMenuToggle>
              <UnreadBadge>
                <Avatar attachment={avatar} />
              </UnreadBadge>
            </IonMenuToggle>
          </IonButtons>
          {children ? children : pageTitleBuilder(title, subtitle)}
          <IonButtons slot="end">
            <SendSentryBreadcrumbsButton />
            {rightButtons}
            {displayBookmarkToggle && (
              <BookmarkToggle title={title ?? ''} type={bookmarkProps.type} />
            )}
          </IonButtons>
        </IonToolbar>
        {secondaryToolbar && (
          <IonToolbar className="secondary-toolbar">
            {secondaryToolbar}
          </IonToolbar>
        )}
      </IonHeader>
    );
  }
);

const NestedPageHeader = forwardRef<HTMLIonHeaderElement, NestedHeaderProps>(
  (props: NestedHeaderProps, ref): ReactElement => {
    const {
      children,
      title,
      subtitle,
      secondaryToolbar,
      rightButtons,
      backgroundColor,
      bookmarkProps,
    } = props;

    const displayBookmarkToggle = bookmarkProps?.enable && title !== undefined;

    return (
      <IonHeader ref={ref} className="primary-header">
        <IonToolbar
          className="primary-toolbar"
          style={{ '--background': backgroundColor }}
        >
          <IonButtons slot="start">
            <CustomBackButton defaultHref={props.defaultBackRoute} />
          </IonButtons>
          {children ? children : pageTitleBuilder(title, subtitle)}
          <IonButtons slot="end">
            <SendSentryBreadcrumbsButton />
            {rightButtons}
            {displayBookmarkToggle && (
              <BookmarkToggle title={title ?? ''} type={bookmarkProps.type} />
            )}
          </IonButtons>
        </IonToolbar>
        {secondaryToolbar && (
          <IonToolbar
            className="secondary-toolbar"
            style={{ '--background': backgroundColor }}
          >
            {secondaryToolbar}
          </IonToolbar>
        )}
      </IonHeader>
    );
  }
);

const SendSentryBreadcrumbsButton = (): ReactElement | null => {
  const [isShowing, setShowing] = useState<boolean>(false);

  const { i18n } = useTranslation();
  const isButtonEnabled = useHasFeature(`show-sentry-breadcrumb-button`);

  const onClick = useCallback(async () => {
    captureEvent({ message: 'User submitted breadcrumbs' });
    setShowing(true);
  }, []);

  if (isButtonEnabled) {
    return (
      <IonButton onClick={onClick}>
        <IonIcon icon={warningOutline} />
        <IonToast
          isOpen={isShowing}
          onDidDismiss={() => {
            setShowing(false);
          }}
          color={'success'}
          position={'top'}
          message={i18n.t('common:send_sentry_breadcrumbs_success')}
          duration={3000}
        />
      </IonButton>
    );
  } else {
    return null;
  }
};

NestedPageHeader.displayName = 'NestedPageHeader';
PageHeader.displayName = 'PageHeader';

export { NestedPageHeader, PageHeader, pageTitleBuilder };
